<template>
  <div class="relative mx-auto max-w-3xl overflow-hidden rounded-[28px] bg-white shadow-lg print:max-w-none print:rounded-none print:shadow-none">
    <!-- Top bar -->
    <div class="h-2 w-full bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600"></div>

    <div class="p-6 sm:p-10">
      <!-- Scan to view (top right on larger screens / print, stacked above on mobile) -->
      <div v-if="viewUrl" class="mb-6 text-center sm:absolute sm:right-8 sm:top-8 sm:mb-0 print:absolute print:right-8 print:top-8">
        <QrImage :value="viewUrl" :size="88" class="mx-auto h-16 w-16" alt="Scan to view this voucher online" />
        <p class="mt-1 text-[8px] font-semibold uppercase tracking-wide text-gray-400">Scan to View</p>
      </div>

      <!-- Letterhead -->
      <div class="flex flex-col items-start gap-4 border-b border-amber-100 pb-6 sm:flex-row sm:items-start sm:justify-between sm:gap-0 sm:pr-24 print:flex-row print:pr-24">
        <img :src="etimadLogo" alt="Al Etimad Haramain Travel" class="h-12 w-auto sm:h-14" />
        <div class="text-left text-xs leading-relaxed text-gray-500 sm:text-right print:text-right">
          <p>aletimadtravel@gmail.com</p>
          <p>+92 318 5073318</p>
          <p>aletimadtravel.com</p>
        </div>
      </div>

      <!-- Title -->
      <div class="mt-7 flex items-end justify-between">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.4em] text-amber-500">Booking Voucher</p>
          <h1 class="mt-1.5 text-2xl font-bold tracking-tight text-gray-900">{{ voucher.voucherNo }}</h1>
        </div>
        <div class="text-right text-xs text-gray-500">
          <p class="uppercase tracking-wide">Issue Date</p>
          <p class="mt-0.5 font-semibold text-gray-800">{{ voucher.issueDate }}</p>
        </div>
      </div>

      <!-- Customer / Hotel / Booking Type -->
      <div class="mt-7 grid grid-cols-1 gap-4 rounded-2xl bg-gray-50/80 p-5 text-sm sm:grid-cols-3 sm:gap-6 print:grid-cols-3">
        <div>
          <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Customer</p>
          <p class="mt-1 font-semibold text-gray-900">{{ voucher.customerName }}</p>
          <p v-if="voucher.customerPhone" class="text-gray-500">{{ voucher.customerPhone }}</p>
        </div>
        <div>
          <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Hotel Name</p>
          <p class="mt-1 font-bold text-gray-900">{{ voucher.hotelName }}</p>
        </div>
        <div class="sm:text-right print:text-right">
          <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Booking Type</p>
          <p class="mt-1 font-semibold text-gray-900">{{ voucher.bookingType }}</p>
        </div>
      </div>

      <!-- Rooms -->
      <div v-if="(voucher.rooms || []).length" class="mt-5 overflow-x-auto rounded-2xl border border-gray-100">
        <table class="w-full min-w-[640px] text-sm">
          <thead>
            <tr class="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <th class="px-3 py-2 font-semibold">Qty</th>
              <th class="px-3 py-2 font-semibold">Room Type</th>
              <th class="px-3 py-2 font-semibold">View</th>
              <th class="px-3 py-2 font-semibold">Meal</th>
              <th class="px-3 py-2 font-semibold">Check In</th>
              <th class="px-3 py-2 font-semibold">Check Out</th>
              <th class="px-3 py-2 font-semibold">Nights</th>
              <th class="px-3 py-2 font-semibold">HCN</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="(room, i) in voucher.rooms" :key="i">
              <td class="px-3 py-2.5 text-gray-700">{{ room.qty }}</td>
              <td class="px-3 py-2.5 font-medium text-gray-900">{{ room.roomType }}</td>
              <td class="px-3 py-2.5 text-gray-700">{{ room.view }}</td>
              <td class="px-3 py-2.5 text-gray-700">{{ room.meal }}</td>
              <td class="px-3 py-2.5 text-gray-700">{{ formatDate(room.checkIn) }}</td>
              <td class="px-3 py-2.5 text-gray-700">{{ formatDate(room.checkOut) }}</td>
              <td class="px-3 py-2.5 text-gray-700">{{ room.nights }}</td>
              <td class="px-3 py-2.5 text-gray-500">{{ room.hcn || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="(voucher.inclusions || []).length" class="mt-5 text-sm">
        <span class="text-gray-500">Inclusions</span>
        <ul class="mt-1 list-disc pl-5 text-gray-900">
          <li v-for="(inc, i) in voucher.inclusions" :key="i">{{ inc }}</li>
        </ul>
      </div>

      <!-- Something Extra -->
      <div v-if="voucher.hasExtra && (voucher.extraTitle || voucher.extraDescription)" class="mt-7 rounded-2xl bg-gray-50/80 p-5">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Extra</p>
        <p v-if="voucher.extraTitle" class="mt-1 font-semibold text-gray-900">{{ voucher.extraTitle }}</p>
        <p v-if="voucher.extraDescription" class="mt-1 whitespace-pre-line text-sm text-gray-600">{{ voucher.extraDescription }}</p>
      </div>

      <p v-if="voucher.notes" class="mt-7 whitespace-pre-line text-sm text-gray-600">{{ voucher.notes }}</p>

      <div class="mt-10 border-t border-gray-100 pt-6 text-center text-xs text-gray-400">
        Thank you for booking with Al Etimad Haramain Travel. This voucher is proof of your confirmed booking —
        please present it during your journey.
      </div>
    </div>

    <!-- Bottom bar -->
    <div class="h-2 w-full bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600"></div>
  </div>
</template>

<script>
import QrImage from './QrImage.vue'
import etimadLogo from '../assets/images/etimad-logo-wide.png'

export default {
  name: 'VoucherDocument',
  components: { QrImage },
  props: {
    voucher: { type: Object, required: true },
    viewUrl: { type: String, default: '' }
  },
  data() {
    return { etimadLogo }
  },
  methods: {
    formatDate(isoDate) {
      if (!isoDate) return ''
      const date = new Date(`${isoDate}T00:00:00`)
      if (Number.isNaN(date.getTime())) return isoDate
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    }
  }
}
</script>
